/** Where a pre-check editor's lists come from. The pieces never fetch: the tour editor
 *  passes sources over its pickers (`toursApi.picker`), the operator's Pre-check-in tab
 *  passes sources over its own GET (`localOptionSource`). */

/** An option of the operator's lists the editors name and pick: a tag category (by its
 *  `code`) or a waiver (by its `id`). Structurally an `@/api/tours` `PickerOption`. */
export interface PrecheckOption {
  id: number;
  name: string | null;
  code?: string;
}

/** The operator the lists belong to: its id scopes the grid links (`?tenant=`), its code
 *  labels the picker's options (console rule: tenant-owned records show tenant + pk). */
export interface PrecheckTenant {
  id: number;
  code?: string | null;
}

/** A list the editor names stored values from, and the search its add picker runs. */
export interface OptionSource<T extends PrecheckOption = PrecheckOption> {
  /** The operator's list, once loaded (names of stored codes and ids). */
  list: readonly T[] | undefined;
  /** `list` is the whole of it: a stored id missing from it is really not there. */
  complete: boolean;
  /** Loading `list` failed — said with a Retry. */
  failed: boolean;
  retry: () => void;
  /** The add picker's options for a typed query ('' = the first page). */
  search: (query: string) => Promise<T[]>;
}

/** A category's code as the questions store it. */
export const categoryCode = (o: PrecheckOption): string => o.code ?? String(o.id);

/** A category's name by its code, from the operator's list (`null` when not there). */
export function categoryNameOf(list: readonly PrecheckOption[] | undefined): (code: string) => string | null {
  const byCode = new Map((list ?? []).map((o) => [categoryCode(o), o.name]));
  return (code) => byCode.get(code) ?? null;
}

/** A source over a list already in hand (the operator tab's GET): complete, searched
 *  locally by name or code. */
export function localOptionSource<T extends PrecheckOption>(
  list: readonly T[] | undefined,
  state: { failed?: boolean; retry?: () => void } = {},
): OptionSource<T> {
  return {
    list,
    complete: list !== undefined,
    failed: state.failed ?? false,
    retry: state.retry ?? (() => undefined),
    search: async (query) => {
      const q = query.trim().toLowerCase();
      const all = [...(list ?? [])];
      return q === '' ? all : all.filter((o) => (o.name ?? '').toLowerCase().includes(q) || (o.code ?? '').toLowerCase().includes(q));
    },
  };
}
